import React, { useState, useEffect, Fragment } from 'react';
import { connect } from 'react-redux';
import { useImmer } from 'use-immer';
import lodash from 'lodash';
import Input from 'components/common/Input/FormInput';
import InputField from 'components/common/Study/InputField';
import DismissiblePanel, { DismissibleHeader } from 'components/common/DismissiblePanel';
import StudyMonitorForm from 'components/common/Study/StudyMonitorForm';
import { PageDescription } from 'components/common/Study/PageComponents';
import { Button, BUTTON } from 'components/common/Button/Button';
import styles from './StudyMonitors.scss';
import { LoadingStatus } from 'components/common/LoadingStatus/LoadingStatus';
import { loadExternalUserForStudy, clearExternalUserForStudy } from 'pages/studyOverview/actions/studySettings';

// RFC 5322 (mostly) compliant "practical" regex for practical email validation
// https://www.regular-expressions.info/email.html
const EMAIL_ADDRESS_PATTERN = /^[a-z0-9!#$%&'*+/=?^_‘{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_‘{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

const NEW_USER = 'This user does not have an account in Complion. Please complete the following information so we can create an account and send an email to this user.';
const EXISTING_USER = 'This user already has an account in Complion. Please provide the window of access to add them to this study.';
const EDIT_USER = 'Modify the monitor\'s window of access to this study.';

const screens = Object.freeze({
    EMAIL_SCREEN: 'email_screen',
    DETAIL_SCREEN: 'detail_screen'
});

const ActionRow = ({ onCancel, onPrimaryAction, primaryActionDisabled, actionButtonLabel }) => (
    <div className={styles.actionRow} data-cy="AddMonitorPanel">
        <Button
            bgcolor={BUTTON.BGCOLOR.CLEAR}
            color={BUTTON.COLOR.BLACK}
            type={BUTTON.SIZE.SECONDARY}
            onClick={onCancel}
            data-cy="AddMonitorPanel-Cancel"
        >
            Cancel
        </Button>
        <Button
            bgcolor={BUTTON.BGCOLOR.ORANGE}
            color={BUTTON.COLOR.WHITE}
            onClick={onPrimaryAction}
            disabled={primaryActionDisabled}
            data-cy="AddMonitorPanel-actionButtonLabel"
        >
            {actionButtonLabel}
        </Button>
    </div>
);

const EmailScreen = (props) => {
    const {
        checkIfMonitorIsExistingUser,
        onDismiss,
        setCurrentScreen,
    } = props;

    const [email, setEmail] = useState('');

    const emailMatchesExpectedPattern = () => {
        return EMAIL_ADDRESS_PATTERN.test(email);
    };

    const updateEmailState = (e) => {
        setEmail(e.target.value.toLowerCase());
    };

    const missingRequiredFields = () => {
        return !email;
    };

    const allInputFieldsComplete = () => {
        return !missingRequiredFields() && emailMatchesExpectedPattern();
    };

    const handleNextAction = () => {
        checkIfMonitorIsExistingUser(email);
        setCurrentScreen(screens.DETAIL_SCREEN);
    };

    const checkFieldsComplete = () => {
        if (allInputFieldsComplete()) {
            handleNextAction();
        }
    };

    return (
        <Fragment data-cy="AddMonitorPanel-Fragment">
            <PageDescription>Please provide the email address for the person you wish to add.</PageDescription>
            <InputField required={false}>
                <Input
                    title={'Email Address'}
                    value={email}
                    placeholder={'email@example.com'}
                    required={true}
                    isValid={emailMatchesExpectedPattern}
                    errorText={'Please fill in a valid email address'}
                    onBlur={updateEmailState}
                    onChange={updateEmailState}
                    autoFocus={true}
                    onClick={checkFieldsComplete}
                    data-cy="AddMonitorPanel-Email"
                />
            </InputField>
            <div className={styles.addMonitorPanelContents} data-cy="AddMonitorPanel-div">
                <ActionRow
                    onCancel={onDismiss}
                    actionButtonLabel='Next'
                    onPrimaryAction={handleNextAction}
                    primaryActionDisabled={!allInputFieldsComplete()}
                />
            </div>
        </Fragment>
    );
};

const DetailScreen = (props) => {
    const {
        onAction,
        onDismiss,
        editMode,
        endDateIsValid,
        endTimeIsValid,
        externalUser,
        inputFieldsComplete,
        isExistingExternalUser,
        updateExternalUserInputs,
        searchingStudyMonitors,
    } = props;

    if (searchingStudyMonitors) {
        return (
            <LoadingStatus
                actionLabel={'searching'}
                itemName={'Study Monitors'}
                filterQuery={false}
                loading
            />
        );
    }

    return (
        <div className={styles.addMonitorPanelContents} data-cy="AddMonitorPanel-return">
            {!editMode && <PageDescription>{isExistingExternalUser ? EXISTING_USER : NEW_USER}</PageDescription>}
            {editMode && <PageDescription>{EDIT_USER}</PageDescription>}
            <StudyMonitorForm
                externalUser={externalUser}
                isExistingExternalUser={isExistingExternalUser}
                updateExternalUserInputs={updateExternalUserInputs}
                endDateIsValid={endDateIsValid}
                endTimeIsValid={endTimeIsValid}
            />
            <div></div>
            <ActionRow
                onCancel={onDismiss}
                actionButtonLabel={!editMode ? 'Add Study Monitor' : 'Save Changes'}
                onPrimaryAction={onAction.bind(null, externalUser)}
                primaryActionDisabled={!inputFieldsComplete}
            />
        </div>
    );
};

export const AddMonitorPanel = props => {
    const {
        clearExternalUserForStudy,
        dismiss,
        existingExternalUser,
        loadExternalUserForStudy,
        onAction,
        onDismiss,
        monitorToEdit,
        searchingStudyMonitors,
    } = props;

    const existingUser = lodash.isEmpty(monitorToEdit) ? existingExternalUser : monitorToEdit;
    const [externalUser, setExternalUser] = useImmer(existingUser || {});
    const [currentScreen, setCurrentScreen] = useState(lodash.isEmpty(monitorToEdit) ? screens.EMAIL_SCREEN : screens.DETAIL_SCREEN);
    const [inputFieldsComplete, setInputFieldsComplete] = useState(false);

    useEffect(() => {
        setExternalUser(draft => draft = existingUser);
        setCurrentScreen(draft => draft = !lodash.isEmpty(monitorToEdit) ? screens.DETAIL_SCREEN : currentScreen);
    }, [existingExternalUser, monitorToEdit]);

    useEffect(() => {
        allFieldValues();
    }, [externalUser]);

    const checkIfMonitorIsExistingUser = value => loadExternalUserForStudy(value);

    const updateExternalUserInputs = (key, value) => {
        setExternalUser(draft => {
            draft[key] = value;
        });

        allFieldValues();
    };

    const isValid = (name) => !lodash.isEmpty(externalUser[name]);

    const startDate = externalUser['startDate'];
    const endDate = externalUser['endDate'];
    const startTime = externalUser['startTime'];
    const endTime = externalUser['endTime'];

    const endDateIsValid = () => {
        if (startDate && endDate) {
            const startDateIsGreaterThanEndDate = startDate > endDate;

            if (startDateIsGreaterThanEndDate) {
                return false;
            }
        }

        return true;
    };

    const endTimeIsValid = () => {
        if (startDate && startTime && endDate && endTime) {
            const startTimeIsGreaterThanOrEqualToEndTime = startTime >= endTime;

            const startDateEqualsEndDate = startDate === endDate;

            if (startDateEqualsEndDate && startTimeIsGreaterThanOrEqualToEndTime) {
                return false;
            }
        }

        return true;
    };

    const allFieldValues = () => {
        if (isValid('emailAddress') &&
            isValid('firstName') &&
            isValid('lastName') &&
            isValid('startDate') &&
            isValid('startTime') &&
            endDateIsValid() &&
            endTimeIsValid()) {
            setInputFieldsComplete(true);
        } else {
            setInputFieldsComplete(false);
        }
    };

    const dismissAll = () => {
        clearExternalUserForStudy();
        setExternalUser(draft => draft = {});
        onDismiss();
    };

    const isExistingExternalUser = existingUser && existingUser.firstName;

    const getCurrentScreen = () => {
        switch (currentScreen) {
            case screens.EMAIL_SCREEN:
                return <EmailScreen
                    checkIfMonitorIsExistingUser={checkIfMonitorIsExistingUser}
                    setCurrentScreen={setCurrentScreen}
                    onDismiss={dismissAll}
                />;

            case screens.DETAIL_SCREEN:
                return <DetailScreen
                    editMode={!lodash.isEmpty(monitorToEdit)}
                    externalUser={externalUser}
                    isExistingExternalUser={isExistingExternalUser}
                    onAction={onAction}
                    updateExternalUserInputs={updateExternalUserInputs}
                    inputFieldsComplete={inputFieldsComplete}
                    searchingStudyMonitors={searchingStudyMonitors}
                    onDismiss={dismissAll}
                    endDateIsValid={endDateIsValid}
                    endTimeIsValid={endTimeIsValid}
                />;

            default:
                return null;
        }
    };

    return (
        <DismissiblePanel
            className={styles.addMonitorPanel}
            dismiss={!dismiss}
            onDismiss={dismissAll}
        >
            <DismissibleHeader
                title={lodash.isEmpty(monitorToEdit) ? 'Add Study Monitor' : 'Edit Study Monitor'}
            />
            {getCurrentScreen()}
        </DismissiblePanel>
    );
};

export default connect(state => {
    const existingExternalUser = lodash.get(state, 'existingExternalUser', {});
    const searchingStudyMonitors = lodash.get(state, 'existingExternalUser.loading', false);
    const studyMonitors = lodash.get(state, 'externalUsersForStudy', []);

    return {
        existingExternalUser: formatExistingExternalUserDates(existingExternalUser, studyMonitors),
        searchingStudyMonitors,
    };
}, {
    loadExternalUserForStudy,
    clearExternalUserForStudy,
})(AddMonitorPanel);

function formatExistingExternalUserDates(existingExternalUser, studyMonitors) {
    const existingStudyMonitor = studyMonitors.find(studyMonitor => studyMonitor.id === existingExternalUser.id);

    return existingStudyMonitor || existingExternalUser;
}
